/**
 * Error Type Definitions
 *
 * Hierarchical error types for the action orchestrator system.
 * All errors extend from ActionError for consistent handling.
 */

import { ResponseCode } from './response-codes.types';

/**
 * Category of error for classification and handling.
 */
export enum ErrorCategory {
  /** Validation errors (invalid input) */
  VALIDATION = 'validation',
  /** API/network errors */
  API = 'api',
  /** Authentication/authorization errors */
  AUTHENTICATION = 'authentication',
  /** Internal system errors */
  INTERNAL = 'internal',
  /** Unknown/unclassified errors */
  UNKNOWN = 'unknown',
}

/**
 * Base error class for all action orchestrator errors.
 * Provides consistent structure for error handling across the system.
 */
export class ActionError extends Error {
  /** Error category for classification */
  public readonly category: ErrorCategory;
  /** Specific error code (e.g., ResponseCode.INVALID_CREDENTIALS, ResponseCode.NETWORK_ERROR) */
  public readonly code: string;
  /** Additional error details */
  public readonly details?: Record<string, unknown>;
  /** Original error that caused this error (if any) */
  public readonly cause?: Error;

  constructor(
    message: string,
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    code: string = 'UNKNOWN_ERROR',
    details?: Record<string, unknown>,
    cause?: Error
  ) {
    super(message);
    this.name = 'ActionError';
    this.category = category;
    this.code = code;
    this.details = details;
    this.cause = cause;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (
      'captureStackTrace' in Error &&
      typeof (Error as any).captureStackTrace === 'function'
    ) {
      (Error as any).captureStackTrace(this, ActionError);
    }
  }

  /**
   * Convert error to JSON-serializable object.
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      category: this.category,
      code: this.code,
      details: this.details,
      stack: this.stack,
    };
  }
}

/**
 * Error thrown when input validation fails.
 */
export class ValidationError extends ActionError {
  /** Field that failed validation */
  public readonly field?: string;
  /** Validation constraint that was violated */
  public readonly constraint?: string;

  constructor(
    message: string,
    field?: string,
    constraint?: string,
    details?: Record<string, unknown>
  ) {
    super(message, ErrorCategory.VALIDATION, 'VALIDATION_ERROR', {
      ...details,
      field,
      constraint,
    });
    this.name = 'ValidationError';
    this.field = field;
    this.constraint = constraint;
  }
}

/**
 * Error thrown when API request fails.
 */
export class ApiError extends ActionError {
  /** HTTP status code */
  public readonly statusCode?: number;
  /** API endpoint that failed */
  public readonly endpoint?: string;

  constructor(
    message: string,
    statusCode?: number,
    endpoint?: string,
    details?: Record<string, unknown>,
    cause?: Error
  ) {
    super(
      message,
      ErrorCategory.API,
      'API_ERROR',
      {
        ...details,
        statusCode,
        endpoint,
      },
      cause
    );
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.endpoint = endpoint;
  }
}

/**
 * Error thrown when authentication fails.
 */
export class AuthenticationError extends ActionError {
  /** Type of authentication failure */
  public readonly authType?: 'login' | 'token' | 'session' | 'permission';

  constructor(
    message: string,
    authType?: 'login' | 'token' | 'session' | 'permission',
    details?: Record<string, unknown>
  ) {
    super(message, ErrorCategory.AUTHENTICATION, 'AUTHENTICATION_ERROR', {
      ...details,
      authType,
    });
    this.name = 'AuthenticationError';
    this.authType = authType;
  }
}

/**
 * Error thrown when an operation fails due to a response code.
 * Used in async/polling workflow for business logic validation failures.
 */
export class ResponseCodeError extends ActionError {
  /** Response code identifying the failure type */
  public readonly responseCode: string;
  /** HTTP status code associated with this error */
  public readonly statusCode?: number;

  constructor(
    message: string,
    responseCode: string,
    statusCode?: number,
    details?: Record<string, unknown>
  ) {
    super(message, ErrorCategory.API, responseCode, {
      ...details,
      responseCode,
      statusCode,
    });
    this.name = 'ResponseCodeError';
    this.responseCode = responseCode;
    this.statusCode = statusCode;
  }
}

/**
 * Error thrown when network connectivity issues occur.
 * Indicates the request couldn't reach the server.
 */
export class NetworkError extends ActionError {
  /** Type of network failure */
  public readonly networkType?: 'offline' | 'dns' | 'connection' | 'unknown';

  constructor(
    message: string,
    networkType?: 'offline' | 'dns' | 'connection' | 'unknown',
    details?: Record<string, unknown>,
    cause?: Error
  ) {
    super(
      message,
      ErrorCategory.API,
      ResponseCode.NETWORK_ERROR,
      {
        ...details,
        networkType,
      },
      cause
    );
    this.name = 'NetworkError';
    this.networkType = networkType;
  }
}

/**
 * Error thrown when an operation exceeds its time limit.
 * Indicates the request took too long to complete.
 */
export class TimeoutError extends ActionError {
  /** Timeout duration in milliseconds */
  public readonly timeoutMs?: number;
  /** Type of timeout (request, polling, operation) */
  public readonly timeoutType?: 'request' | 'polling' | 'operation';

  constructor(
    message: string,
    timeoutMs?: number,
    timeoutType?: 'request' | 'polling' | 'operation',
    details?: Record<string, unknown>
  ) {
    super(message, ErrorCategory.API, ResponseCode.TIMEOUT_ERROR, {
      ...details,
      timeoutMs,
      timeoutType,
    });
    this.name = 'TimeoutError';
    this.timeoutMs = timeoutMs;
    this.timeoutType = timeoutType;
  }
}
